import React from 'react';
import type { Editor } from 'ckeditor5';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { OEToggle } from '../../../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../../../global/text/text';
import { InfoPopover } from '../../../../../../global/info-popover/info-popover';
import { FormField } from '../../../../../../global/form/form-field/form-field';
import { RichTextEditor } from '../../../../../../global/rich-text-editor';
import { EnablementStatus } from '../../../../../../../constants/common.constants';
import { LOADING, REQUIRED_FIELD_TEXT } from '../../../../../../../constants/common-labels.constants';
import {
    HUB_ONE_STEP_SIGN_IN_INFO_TEXT,
    HUB_ONE_STEP_SIGN_IN_TOGGLE_TEXT,
    HUB_SIGN_IN_DISPLAY_NAME_LABEL_TEXT,
    HUB_SIGN_IN_LINK_REQUESTED_MESSAGE_LABEL_TEXT,
    HUB_SIGN_IN_REQUEST_LINK_BUTTON_LABEL_TEXT,
    HUB_SIGN_IN_REQUEST_LINK_INSTRUCTIONS_LABEL_TEXT,
    HUB_SIGN_IN_TEXT,
} from '../../../../../event-hubs.text';
import { HubSignInSettingsVM } from './hub-sign-in-settings.viewmodel';
import './hub-sign-in-settings.scss';

type HubSignInSettingsProps = {
    vm: HubSignInSettingsVM;
};

export const HubSignInSettings: React.FC<HubSignInSettingsProps> = observer(({ vm }) => {
    if (vm.isLoading()) {
        return (
            <div className='hub-sign-in-settings__loading'>
                <FormattedMessage {...LOADING} values={{ suffix: '...' }} />
            </div>
        );
    }

    return (
        <div className='hub-sign-in-settings'>
            <div className='hub-sign-in-settings__page-copy'>
                <div className='hub-sign-in-settings__toggle-row'>
                    <OEToggle
                        isSelected={vm.isOneStepSignInEnabled()}
                        onClick={vm.toggleOneStepSignInEnabled}
                        dataTestId='toggle-hub-one-step-sign-in-enabled'
                    />
                    <Text
                        message={vm.intl.formatMessage(HUB_ONE_STEP_SIGN_IN_TOGGLE_TEXT, {
                            suffix: vm.isOneStepSignInEnabled()
                                ? EnablementStatus.ENABLED
                                : EnablementStatus.DISABLED,
                        })}
                    />
                    <InfoPopover className='m-left-8px'>
                        <FormattedMessage {...HUB_ONE_STEP_SIGN_IN_INFO_TEXT} />
                    </InfoPopover>
                </div>

                <FormField
                    className='hub-sign-in-settings__field'
                    label={<FormattedMessage {...HUB_SIGN_IN_DISPLAY_NAME_LABEL_TEXT} />}
                >
                    <input
                        className='oe-form-field__control'
                        type='text'
                        value={vm.getDisplayName()}
                        placeholder={vm.intl.formatMessage(HUB_SIGN_IN_TEXT)}
                        onChange={event => vm.setDisplayName(event.target.value)}
                        data-test-id='hub-sign-in-display-name'
                    />
                    {vm.getDisplayNameError() &&
                        <span className='oe-form-field__error'>{vm.getDisplayNameError()}</span>}
                </FormField>

                <FormField
                    className='hub-sign-in-settings__field'
                    label={<FormattedMessage {...HUB_SIGN_IN_REQUEST_LINK_INSTRUCTIONS_LABEL_TEXT} />}
                >
                    <RichTextEditor
                        className='oe-form-field__control m-0px p-0px'
                        value={vm.getRequestLinkInstructions()}
                        onChange={(_: unknown, editor: Editor) => vm.setRequestLinkInstructions(editor.getData())}
                        validationError={vm.getRequestLinkInstructionsError() || undefined}
                        dataTestId='hub-sign-in-request-link-instructions'
                    />
                </FormField>

                <FormField
                    className='hub-sign-in-settings__field'
                    label={<>
                        <FormattedMessage {...HUB_SIGN_IN_REQUEST_LINK_BUTTON_LABEL_TEXT} />
                        <span className='oe-form-field__title-required'><FormattedMessage {...REQUIRED_FIELD_TEXT} /></span>
                    </>}
                >
                    <input
                        className='oe-form-field__control'
                        type='text'
                        value={vm.getRequestLinkButtonLabel()}
                        onChange={event => vm.setRequestLinkButtonLabel(event.target.value)}
                        onBlur={vm.markRequestLinkButtonLabelTouched}
                        data-test-id='hub-sign-in-request-link-button-label'
                    />
                    {vm.getRequestLinkButtonLabelError() &&
                        <span className='oe-form-field__error'>{vm.getRequestLinkButtonLabelError()}</span>}
                </FormField>

                <FormField
                    className='hub-sign-in-settings__field'
                    label={<>
                        <FormattedMessage {...HUB_SIGN_IN_LINK_REQUESTED_MESSAGE_LABEL_TEXT} />
                        <span className='oe-form-field__title-required'><FormattedMessage {...REQUIRED_FIELD_TEXT} /></span>
                    </>}
                >
                    <RichTextEditor
                        className='oe-form-field__control m-0px p-0px'
                        value={vm.getSignInLinkRequestedMessage()}
                        onChange={(_: unknown, editor: Editor) => vm.setSignInLinkRequestedMessage(editor.getData())}
                        onBlur={vm.markSignInLinkRequestedMessageTouched}
                        validationError={vm.getSignInLinkRequestedMessageError() || undefined}
                        dataTestId='hub-sign-in-link-requested-message'
                    />
                </FormField>
            </div>
        </div>
    );
});
